import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import clsx from 'clsx';
import { FaCheck, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Paper } from '@material-ui/core';
import { MdInfo } from 'react-icons/md';
import { useAnalytics } from 'use-analytics';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { GoLinkExternal } from 'react-icons/go';
import HotelDetails from '../../HotelDetails';
import useStyles from '../styles';
import Button from '../../../../Button';
import Label from '../../../../Label';
import {
  HotelProviders,
  HotelRateDisplayPreferences,
  HotelRateUnavailabilityTypes,
  PageLinks,
  SearchModes,
  SearchStatus,
  tryCatchWrapper,
} from '../../../../../constants';
import useScrollToElement from '../../../../../hooks/useScrollToElement';
import DistancesToHotel from '../../DistancesToHotel';
import AgentPreferencesInfo from '../AgentPreferencesInfo';
import RateDetailDropdownPopover from '../RateDetailDropdownPopover/index.tsx';
import HotelMatchWithSearch from '../HotelMatchWithSearch';
import getHotelRateToDisplay from '../../../../../helpers/getHotelRateToDisplay.ts';
import currencySymbol from '../../../../../helpers/currencySymbol';
import getHotelRate from '../../../../../helpers/selectors/getHotelRate.ts';
import HotelAmenities from '../HotelAmenities';
import RemoveFromCompare from '../../../RemoveFromCompare/index';
import hotelSearchInput from '../../../../../helpers/selectors/hotelSearchInput';
import { hotelSearch } from '../../../../../store/slices/hotel';
import {
  hotelSearchRequest,
  setHotelTripSearchInputs,
} from '../../../../../store/slices/tripSearch';
import ToolTip from '../../../../ToolTip';
import NightlyPriceDisplay from '../../HotelDetails/NightlyPriceDisplay';
import HotelProviderLabel from '../HotelProviderLabel';
import useBestAvailableRatesOption from '../../../../../hooks/useBestAvailableRatesOption';

const HotelItineraryContent = ({
  itinerary,
  tripId,
  onModalClose,
  isInsideModal,
  isExpanded,
  onMouseEnter,
  onMouseLeave,
  doShowPrice,
  doScrollToHotel,
  doShowAgentCommission,
  doHaveBorderAround,
  wrapperClassName,
  isHotelSelected,
  pricingPreference,
  isEmailView,
  doShowChatGpt,
  doShowMarkupRates,
  doShowHotelSourceInHotelName,
  doCleanseAmenities,
  travelOption,
  tripRoomSearchInput,
  compactImage,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const { advisor, debug } = router?.query || {};
  const doShowDebug = debug === 'hotel';
  const { isViewingCompare } = useSelector((state) => state.compare);
  const { travelAgency = {}, isLoggedIn } = useSelector((state) => state.user);
  const { isViewingBrochure } = useSelector((state) => state.brochure);

  const selectedSearchMode = useSelector((store) => store.tripSearch.selectedSearchMode);
  // Search-bar mode; not interchangeable with `isHotelTripSearch` above, which keys off tripId.
  const isHotelTripSearch = selectedSearchMode === SearchModes.HOTEL || !tripId;

  const hotelUnavailabilityPlaceholder = travelAgency?.settings?.hotelUnavailabilityPlaceholder;

  // Agencies restricted to preferred rates hide the best available rates option,
  // so we must not offer a way to search without preferred rate codes.
  const { isBestAvailableRatesOptionEnabled } = useBestAvailableRatesOption(tripId);
  const isHotelSearchInProgress = useSelector((store) =>
    isHotelTripSearch
      ? store.tripSearch.searchStatus === SearchStatus.SEARCHING
      : store.hotel.hotelSearches[tripId]?.searchStatus === SearchStatus.SEARCHING,
  );

  const rateUnavailability = itinerary.hotel?.hotelRateUnavailabilities?.[0];
  const arePreferredRatesUnavailable =
    rateUnavailability?.type === HotelRateUnavailabilityTypes.NoPreferredRates;
  const unavailabilityLabel = arePreferredRatesUnavailable
    ? 'Preferred Rates Not Available'
    : 'Not Available';
  // Tooltip and button are gated on the same flag, so the tooltip can never point at a control
  // that is not on screen: the preferences checkbox is worded differently for agencies without
  // the redesigned rate codes filter, and is not rendered at all for some of them.
  const doShowBestAvailableRatesButton =
    arePreferredRatesUnavailable &&
    isBestAvailableRatesOptionEnabled &&
    !isEmailView &&
    !isViewingBrochure &&
    !isViewingCompare;

  const getUnavailabilityTooltip = () => {
    if (!arePreferredRatesUnavailable)
      return rateUnavailability?.displayMessage || rateUnavailability?.reason;

    if (!doShowBestAvailableRatesButton)
      return 'No preferred/program rates were returned for this property for the selected dates or search criteria.';

    return 'No preferred/program rates were returned for this property. It may still be bookable at standard rates - use "Search Best Available Rates" below to include non-preferred rates. Client perks may not apply.';
  };

  const unavailabilityTooltip = getUnavailabilityTooltip();

  const handleSearchBestAvailableRates = () => {
    if (isHotelSearchInProgress) return;
    const searchObj = { page: 1, exactMatchRateCodesOnly: false };

    if (isHotelTripSearch) {
      dispatch(setHotelTripSearchInputs(searchObj));
      dispatch(hotelSearchRequest(searchObj));
    } else {
      dispatch(hotelSearch({ tripId, searchObj }));
    }
    track('hotelUnavailability.searchBestAvailableRates', {
      hotelId: itinerary.hotel.providerId,
    });
  };
  const doHideAmenitiesBelowHotelName =
    travelAgency && travelAgency.settings && travelAgency.settings.doHideAmenitiesBelowHotelName;

  const { pageName } = router?.query || {};
  const changeHotelPage = pageName === PageLinks.ChangeHotel.pageName;
  const selectedHotelLabel = changeHotelPage ? 'Booked Hotel' : 'Selected Hotel';

  const doExpandSearchedHotel =
    travelAgency && travelAgency.settings && travelAgency.settings.doExpandSearchedHotel;
  const doDisplayHotelContactInfo =
    travelAgency && travelAgency.settings && travelAgency.settings.doDisplayHotelContactInfo;

  const { selectedAddress } = useSelector(hotelSearchInput(tripId, isHotelTripSearch));
  const doHideViewDetailsButton =
    travelAgency && travelAgency.settings && travelAgency.settings.doHideViewDetailsButton;
  const viewHotelRatesButtonLabel =
    travelAgency && travelAgency.settings && travelAgency.settings.viewHotelRatesButtonLabel;

  const brochureViewHotelRatesBtnLabel =
    travelAgency?.settings?.brochureSettings?.brochureViewHotelRatesBtnLabel;
  const brochureHideHotelRatesBtnLabel =
    travelAgency?.settings?.brochureSettings?.brochureHideHotelRatesBtnLabel;

  const isConfirmationPage = (router?.pathname || '').indexOf('confirmation') > -1;

  const classNames = {
    imgContainerClass: clsx('overflow-hidden h-40 w-40 align-middle md:flex-shrink-0'),
    dropdownButtonClass: clsx('text-theme mt-1 focus:outline-none'),
  };
  const hotelItineraryEl = useScrollToElement({ value: doScrollToHotel && isExpanded.value });
  const selectedProduct =
    itinerary.selectedProduct ||
    (itinerary.hotel && itinerary.hotel.products && itinerary.hotel.products[0]);
  // TODO: tripId - refactor to avoid prop drilling
  const isBrochurePage = (router?.pathname || '').indexOf('brochure') > -1;
  const brochureSettings = travelAgency?.settings?.brochureSettings || {};
  const doExpandItineraryDetailsByDefault =
    !!brochureSettings?.doExpandItineraryDetailsByDefault && isBrochurePage;
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [doAutoScroll, setDoAutoScroll] = useState(true);
  const classes = useStyles();
  // TODO: generateHtmlFromComponent failed to generate html when track is uncommented

  const trackHotelDetailsToggle = () => {
    track('hotelItinerary.handleHotelDetailsToggle', {
      travelAgencyId: travelAgency?.id,
      hotelName: itinerary.hotel.name,
      provider: itinerary.hotel.priceProvider,
      providerId: itinerary.hotel.providerId,
    });
  };

  const handleHotelDetailsToggle = () => {
    setDoAutoScroll(true);
    setIsDetailsOpen((v) => !v);
    tryCatchWrapper(() => trackHotelDetailsToggle())();
  };

  const handleMouseEnter = useCallback(() => {
    if (onMouseEnter) {
      const { hotel } = itinerary;
      onMouseEnter(hotel.hotelKey);
    }
  }, [itinerary, onMouseEnter]);

  const handleMouseLeave = useCallback(() => {
    if (onMouseLeave) {
      onMouseLeave(null);
    }
  }, [onMouseLeave]);

  // Apply slicing and joining
  const productPricingElement = useMemo(() => {
    if (!selectedProduct) return null;
    const hotelRate = getHotelRate({
      rate: selectedProduct.roomPrice,
      doShowMarkupRate: doShowMarkupRates,
    });
    const rateToDisplay = getHotelRateToDisplay(pricingPreference, hotelRate);

    let packageRate = 0;
    if (!doShowMarkupRates) {
      const rate = getHotelRate({
        rate: selectedProduct.roomPrice,
        doShowMarkupRate: true,
      });
      packageRate = getHotelRateToDisplay(pricingPreference, rate);
      packageRate = packageRate !== rateToDisplay ? packageRate : 0;
    }
    return (
      <>
        {pricingPreference === HotelRateDisplayPreferences.TotalRate ? (
          <span
            className={clsx('text-smallerHeading font-smallerHeading', { block: packageRate > 0 })}
          >
            <div
              className={clsx(
                'hidden email-hidden-field',
                packageRate === 0 ? 'md:block' : 'md:inline-block',
              )}
            >
              Total Stay
            </div>
            <span className="inline md:hidden text-smallerHeading font-smallerHeading">
              Total stay
            </span>{' '}
            from
          </span>
        ) : (
          <span className="text-smallerHeading font-smallerHeading">from</span>
        )}
        {packageRate > 0 && (
          <NumberFormat
            value={packageRate}
            className="line-through"
            displayType="text"
            thousandSeparator
            prefix={`${currencySymbol(selectedProduct.roomPrice.currencyCode)}`}
            fixedDecimalScale
            decimalScale={2}
          />
        )}
        <NumberFormat
          value={rateToDisplay}
          displayType="text"
          thousandSeparator
          prefix={` ${currencySymbol(selectedProduct.roomPrice.currencyCode)}`}
          fixedDecimalScale
          decimalScale={2}
        />
        {packageRate > 0 && (
          <div className="sm:text-xs xl:text-sm text-gray-600 font-normal mb-1">
            Package discount
          </div>
        )}
      </>
    );
  }, [doShowMarkupRates, pricingPreference, selectedProduct]);

  // isExpanded should be an object
  // needs a new value even if the same marker is clicked
  useEffect(() => {
    if (isExpanded.value) {
      setIsDetailsOpen(true);
    }
  }, [isExpanded]);

  useEffect(() => {
    const isDefaultExpanded = doExpandItineraryDetailsByDefault;
    const isMatchingSearchedHotel =
      doExpandSearchedHotel && selectedAddress?.hotelId === itinerary.hotel.providerId;
    const shouldOpenDetailsWithoutScroll = isDefaultExpanded || isMatchingSearchedHotel;
    if (!shouldOpenDetailsWithoutScroll) return;
    setIsDetailsOpen(true);
    setDoAutoScroll(false);
  }, [
    doExpandItineraryDetailsByDefault,
    doExpandSearchedHotel,
    itinerary.hotel.providerId,
    selectedAddress,
  ]);

  const hotelPageUrl = useMemo(() => {
    const doShowDedicatedHotelPageLink = isLoggedIn
      ? travelAgency?.settings?.doShowDedicatedHotelPageLink
      : travelAgency?.settings?.doShowDedicatedHotelPageLinkCFP;

    const { hotel } = itinerary;
    const doReturnHotelPageUrl =
      (debug === 'hotellink' || doShowDedicatedHotelPageLink) &&
      hotel &&
      hotel.providerId &&
      hotel.name &&
      hotel.priceProvider === HotelProviders.SABRE_LODGING;

    if (!doReturnHotelPageUrl) return undefined;
    const { providerId, name, city } = itinerary.hotel;
    let url = `/hotel/${providerId}/${name.toLowerCase().replace(/\s/g, '-')}`;
    if (city && !name.toLowerCase().includes(city.toLowerCase())) {
      url += `-${city.toLowerCase().replace(/\s/g, '-')}`;
    }
    if (advisor) url += `?advisor=${advisor}`;
    return url;
  }, [itinerary, advisor, debug, isLoggedIn, travelAgency]);
  const doShowDedicatedHotelPageLink = Boolean(hotelPageUrl);

  return (
    <div
      ref={hotelItineraryEl}
      className={clsx(
        'grid grid-cols-4 gap-2 sm:gap-3',
        compactImage && 'items-start',
        wrapperClassName,
        {
          'border-2': doHaveBorderAround,
        },
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="col-span-4 md:col-span-3 md:flex gap-x-3">
        {itinerary.hotel.image ? (
          <img
            src={itinerary.hotel.image}
            alt={itinerary.hotel.name}
            className={clsx(
              'email-img w-full md:w-hotel-img object-cover',
              compactImage ? 'md:h-48 rounded' : 'md:h-hotel-img',
            )}
          />
        ) : (
          <Paper
            square
            classes={classes}
            className="email-hidden-field flex-shrink-0 md:h-hotel-img md:w-hotel-img"
          >
            No photo
          </Paper>
        )}
        <div className="px-4 md:px-2 py-2 min-w-0">
          <div className="font-bold">
            <span className="font-subHeading text-subHeading">
              {itinerary.hotel.name}
              {doShowHotelSourceInHotelName &&
                itinerary.hotel.hotelChainName &&
                ` (${itinerary.hotel.hotelChainName})`}
            </span>
          </div>
          {!isEmailView && (
            <HotelMatchWithSearch hotelId={itinerary.hotel.providerId} tripId={tripId} />
          )}
          {itinerary.hotel.fullAddress && (
            <div className="text-sm mt-1">
              <span className="text-smallerHeading font-smallerHeading">
                {itinerary.hotel.fullAddress}
              </span>
            </div>
          )}
          <DistancesToHotel
            distanceToAddress={itinerary.driveDistanceToSearchDestination}
            distanceToPort={itinerary.driveDistanceToCruisePort}
            geoName={(itinerary.searchGeoname || {}).name || ''}
            walkingDistance={itinerary.walkDistanceToSearchDestination}
          />
          {doDisplayHotelContactInfo &&
            itinerary.hotel.hotelContactInfo &&
            (itinerary.hotel.hotelContactInfo.phone || itinerary.hotel.hotelContactInfo.email) && (
              <div className="text-sm" title="Hotel contact number">
                <div className="text-smallerHeading font-smallerHeading">
                  {itinerary.hotel.hotelContactInfo.phone && (
                    <>{itinerary.hotel.hotelContactInfo.phone}</>
                  )}

                  {itinerary.hotel.hotelContactInfo.email && (
                    <>{itinerary.hotel.hotelContactInfo.email}</>
                  )}
                </div>
              </div>
            )}

          <HotelAmenities
            amenities={itinerary.hotel.amenities}
            starRating={itinerary.hotel.starRating}
            doCleanseAmenities={doCleanseAmenities}
            doHideAmenitiesBelowHotelName={doHideAmenitiesBelowHotelName}
          />

          {!isEmailView && (
            <div className="flex gap-2 flex-wrap mt-2 items-center w-full">
              <AgentPreferencesInfo
                isAgentPreferredHotel={itinerary.hotel.isAgentPreferredHotel}
                informationAddedByAgent={itinerary.hotel.preferredHotelInformationAddedByAgent}
                isInsideModal={isInsideModal}
              />
              <HotelProviderLabel itinerary={itinerary} />
              {itinerary.hotel.notesByAgent && doShowPrice && (
                <div className="col-span-4 text-sm">
                  <b>Note:</b> {itinerary.hotel.notesByAgent}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col justify-between items-end col-span-4 md:col-span-1 px-4 py-2">
        <div className="text-right md:inline-block grid grid-cols-2 w-full md:w-auto">
          {doShowPrice && (
            <div
              className={clsx('font-bold leading-tight text-left md:text-right self-center', {
                'text-sm': isInsideModal,
              })}
            >
              {productPricingElement}
              {pricingPreference !== HotelRateDisplayPreferences.TotalRate &&
                productPricingElement && (
                  <NightlyPriceDisplay pricingPreference={pricingPreference} />
                )}
              {!isEmailView && itinerary.selectedProduct && (
                <div className="mb-2">
                  <RateDetailDropdownPopover
                    roomPrice={itinerary.selectedProduct.roomPrice}
                    numberOfNights={itinerary.duration}
                    alignClass="md:justify-end"
                    tripId={tripId}
                    doShowMarkupRates={doShowMarkupRates}
                  />
                </div>
              )}
            </div>
          )}

          {!selectedProduct &&
          itinerary.hotel.hotelRateUnavailabilities &&
          itinerary.hotel.hotelRateUnavailabilities.length ? (
            <div>
              <span
                className={clsx(
                  {
                    'text-yellow-800':
                      !hotelUnavailabilityPlaceholder && arePreferredRatesUnavailable,
                    'text-red-500':
                      !hotelUnavailabilityPlaceholder && !arePreferredRatesUnavailable,
                  },
                  'font-bold italic',
                )}
              >
                {hotelUnavailabilityPlaceholder || unavailabilityLabel}
              </span>
              {!hotelUnavailabilityPlaceholder && (
                <ToolTip
                  disableHoverListener={false}
                  showCloseIcon={false}
                  variant="light"
                  title={unavailabilityTooltip}
                >
                  <sup className={classes.hotelUnavailInfoSup}>
                    <MdInfo
                      size="20"
                      className={clsx(
                        arePreferredRatesUnavailable ? 'text-yellow-800' : 'text-red-500',
                        'inline-block -mt-1 ml-1 font-bold',
                      )}
                    />
                  </sup>
                </ToolTip>
              )}
              {doShowBestAvailableRatesButton && (
                <div className="mt-1">
                  <Button
                    size={Button.buttonSizes.SM}
                    onClick={handleSearchBestAvailableRates}
                    disabled={isHotelSearchInProgress}
                  >
                    <span>Search Best Available Rates</span>
                  </Button>
                </div>
              )}
            </div>
          ) : (
            tripId &&
            !isViewingBrochure &&
            selectedProduct && (
              <div>
                <Button size={Button.buttonSizes.SM} onClick={handleHotelDetailsToggle}>
                  <span>{viewHotelRatesButtonLabel || 'Select Room'}</span>
                </Button>
                <Label
                  text={selectedHotelLabel}
                  level="success"
                  icon={<FaCheck />}
                  doShow={isHotelSelected}
                />
              </div>
            )
          )}
          {/* Don't show "View details" button
          if showing "Select room" button and doHideViewDetailsButton is true.
          FYI, we don't show "select rooms" button on confirmation, brochure page */}
          {(isConfirmationPage ||
            (isViewingBrochure && !doHideViewDetailsButton) ||
            !selectedProduct ||
            !doHideViewDetailsButton) && (
            <div className={clsx({ 'col-start-2': !doShowPrice }, 'col-span-2 md:col-span-1')}>
              <button
                onClick={handleHotelDetailsToggle}
                type="button"
                className={`${classNames.dropdownButtonClass} email-hidden-field`}
              >
                {!isDetailsOpen && (
                  <>
                    <span>
                      {(isViewingBrochure && brochureViewHotelRatesBtnLabel) || 'View Details '}
                    </span>
                    <FaChevronDown className="inline-block" />
                  </>
                )}
                {isDetailsOpen && (
                  <>
                    <span>
                      {(isViewingBrochure && brochureHideHotelRatesBtnLabel) || 'Hide Details '}
                    </span>
                    <FaChevronUp className="inline-block" />
                  </>
                )}
              </button>
            </div>
          )}
          {isViewingCompare && (
            <RemoveFromCompare itineraryId={itinerary.hotelKey} itinerary={itinerary} />
          )}
          {doShowDedicatedHotelPageLink && (
            <div>
              <a className="underline ml-2" href={hotelPageUrl} target="_blank" rel="noreferrer">
                View hotel page
                <GoLinkExternal className="inline ml-1" />
              </a>
            </div>
          )}
        </div>
      </div>

      {doShowDebug && !isEmailView && (
        <div className="text-sm flex col-span-4 gap-2 px-4 pt-2 pb-4 bg-gray-200">
          <div>
            <b>For debugging:</b>
            <div className="mt-1">
              <button
                type="button"
                title="Copy hotel details to clipboard to paste it to baserow"
                className="underline"
                onClick={() =>
                  navigator.clipboard.writeText(
                    `${itinerary.hotel.ids.sabreLodgingId}\t${itinerary.hotel.name}\t${itinerary.hotel.fullAddress}\t${itinerary.hotel.city || ''}\t${itinerary.hotel.stateCode || ''}\t${itinerary.hotel.country || ''}\t${itinerary.hotel.lat}\t${itinerary.hotel.lon}`,
                  )
                }
              >
                Copy to clipboard
              </button>
            </div>
          </div>
          <div>
            <div className="flex gap-2">
              <div>
                <b>Latitude:</b> {itinerary.hotel.lat}
              </div>
              <div>
                <b>Longitude:</b> {itinerary.hotel.lon}
              </div>
            </div>
            <div>
              <div>
                <div>
                  <b>Hotel id:</b> {itinerary.hotel.providerId}
                </div>
                <div>
                  <b>Name:</b> {itinerary.hotel.name}
                </div>
                <div>
                  <b>Address:</b> {itinerary.hotel.fullAddress}
                </div>
                <div className="flex gap-2">
                  <div>
                    <b>City:</b> {itinerary.hotel.city}
                  </div>
                  <div>
                    <b>State:</b> {itinerary.hotel.stateCode}
                  </div>
                  <div>
                    <b>Country code:</b> {itinerary.hotel.country}
                  </div>
                </div>
                <div>
                  <b>Sabre ID:</b> {itinerary.hotel.ids && itinerary.hotel.ids.sabreId}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {(isDetailsOpen || (isViewingBrochure && doHideViewDetailsButton)) && (
        <div className="col-span-4 -mt-3 border-t border-gray-300">
          <HotelDetails
            itinerary={itinerary}
            tripId={tripId}
            onModalClose={isInsideModal ? onModalClose : null}
            doShowPrice={doShowPrice}
            doAutoScrollToTab={!doAutoScroll || !doScrollToHotel || isInsideModal}
            doShowAgentCommission={doShowAgentCommission}
            travelOption={travelOption}
            tripRoomSearchInput={tripRoomSearchInput}
            isEmailView={isEmailView}
            doShowChatGpt={doShowChatGpt}
          />
        </div>
      )}
    </div>
  );
};

HotelItineraryContent.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  tripId: PropTypes.string,
  onModalClose: PropTypes.func,
  doShowPrice: PropTypes.bool,
  isInsideModal: PropTypes.bool,
  isExpanded: PropTypes.shape({
    value: PropTypes.bool,
  }),
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  doScrollToHotel: PropTypes.bool,
  doShowAgentCommission: PropTypes.bool,
  doHaveBorderAround: PropTypes.bool,
  wrapperClassName: PropTypes.string,
  isHotelSelected: PropTypes.bool,
  isEmailView: PropTypes.bool,
  pricingPreference: PropTypes.number,
  doShowChatGpt: PropTypes.bool,
  nlpConstants: PropTypes.shape({}),
  nlpRelevantTerms: PropTypes.shape({}),
  nlpInstructions: PropTypes.shape({}),
  doShowMarkupRates: PropTypes.bool,
  doShowHotelSourceInHotelName: PropTypes.bool,
  doCleanseAmenities: PropTypes.bool,
  travelOption: PropTypes.shape({
    mode: PropTypes.number,
    key: PropTypes.string,
  }),
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
  compactImage: PropTypes.bool,
};

HotelItineraryContent.defaultProps = {
  isInsideModal: false,
  tripId: '',
  onModalClose: undefined,
  doShowPrice: true,
  isExpanded: { value: false },
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  doScrollToHotel: true,
  doShowAgentCommission: true,
  doHaveBorderAround: true,
  wrapperClassName: 'my-3 rounded-lg',
  isHotelSelected: false,
  isEmailView: false,
  pricingPreference: 0,
  doShowChatGpt: true,
  nlpConstants: undefined,
  nlpRelevantTerms: undefined,
  nlpInstructions: undefined,
  doShowMarkupRates: false,
  doShowHotelSourceInHotelName: true,
  doCleanseAmenities: true,
  travelOption: undefined,
  tripRoomSearchInput: undefined,
  compactImage: false,
};

export default React.memo(HotelItineraryContent);
